"use client";

/**
 * Cross-tab single-flight coordination for token refresh (audit Track A, A2).
 *
 * Only one tab/window should rotate the single-use refresh token at a time —
 * the server hard-deletes the old token on rotation, so a concurrent refresh by
 * a second tab loses the race and gets a 401, which historically forced a
 * spurious logout.
 *
 * Preferred mechanism: the Web Locks API (`navigator.locks`), supported by every
 * current evergreen browser (Chrome 69+, Firefox 96+, Safari 15.4+). For older
 * browsers we fall back to a best-effort `localStorage` soft lock whose release
 * is signalled over a `BroadcastChannel`.
 *
 * This is only one layer of defence. The store also (a) coalesces all callers
 * behind a single in-flight promise, (b) re-reads the latest token after
 * acquiring the lock and skips the network call if another tab already rotated,
 * and (c) re-reads + retries once on a lost-race 401. So even if the soft lock
 * occasionally admits two tabs, no session is torn down.
 */

const LOCK_NAME = "token-refresh";
const CHANNEL_NAME = "token-refresh-lock";
const LS_LOCK_KEY = "token-refresh-lock";
// How long a held soft lock is considered valid before it is treated as stale
// (e.g. the holder's tab crashed mid-refresh).
const LOCK_TTL_MS = 15_000;

interface SoftLock {
  owner: string;
  expiresAt: number;
}

/**
 * Run `fn` while holding the cross-tab refresh lock. Resolves with `fn`'s
 * result. Never rejects from the locking layer itself — any failure to acquire
 * a lock degrades to simply running `fn` (the store's other guards keep that
 * safe).
 */
export async function withTokenRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    // The callback's resolved value is propagated by `request`.
    return navigator.locks.request(LOCK_NAME, fn);
  }
  return withSoftLock(fn);
}

async function withSoftLock<T>(fn: () => Promise<T>): Promise<T> {
  if (
    typeof window === "undefined" ||
    typeof localStorage === "undefined" ||
    typeof BroadcastChannel === "undefined"
  ) {
    // No coordination primitive available — rely on the store's other guards.
    return fn();
  }

  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return fn();
  }

  const owner = generateOwnerId();
  try {
    await waitForFreeLock(channel);
    writeLock(owner);
    try {
      return await fn();
    } finally {
      releaseLock(owner, channel);
    }
  } finally {
    try {
      channel.close();
    } catch {
      // ignore
    }
  }
}

/**
 * Resolve once no other tab is actively holding the lock. If a live lock is
 * present, wait for a `release` broadcast or for the lock's TTL to elapse
 * (whichever comes first). Single-shot — never spins.
 */
async function waitForFreeLock(channel: BroadcastChannel): Promise<void> {
  const existing = readLock();
  if (!existing || existing.expiresAt <= Date.now()) {
    return; // free or stale
  }

  const waitMs = Math.max(0, existing.expiresAt - Date.now());
  await new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      channel.removeEventListener("message", onMessage);
      clearTimeout(timer);
      resolve();
    };
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "release") finish();
    };
    channel.addEventListener("message", onMessage);
    const timer = setTimeout(finish, waitMs);
  });
}

function readLock(): SoftLock | null {
  try {
    const raw = localStorage.getItem(LS_LOCK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SoftLock;
    if (typeof parsed?.owner !== "string" || typeof parsed?.expiresAt !== "number") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeLock(owner: string): void {
  try {
    const lock: SoftLock = { owner, expiresAt: Date.now() + LOCK_TTL_MS };
    localStorage.setItem(LS_LOCK_KEY, JSON.stringify(lock));
  } catch {
    // ignore — best effort
  }
}

function releaseLock(owner: string, channel: BroadcastChannel): void {
  try {
    const current = readLock();
    // Only clear the lock if we still own it (a newer holder may have taken over
    // after our TTL elapsed).
    if (!current || current.owner === owner) {
      localStorage.removeItem(LS_LOCK_KEY);
    }
  } catch {
    // ignore
  }
  try {
    channel.postMessage({ type: "release" });
  } catch {
    // ignore
  }
}

function generateOwnerId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
